import { type CostEstimateData } from '@capsicum/features/payments/shared';

import type {
	Decimal,
	FanTokenOfferingCostEstimate,
	FetchFanTokenOfferingLobbyByVoteTokenOfferingIdData,
	Image3DType,
	PaymentCurrencyType
} from '@capsicum/common';

export type FanTokenOfferingData = {
	altConversionRate: Decimal;
	altCurrency: string;
	altCurrencyId: string;
	altMainLedgerAmount: string;
	defaultConversionRate: Decimal;
	defaultCurrency: string;
	defaultCurrencyId: string;
	defaultMainLedgerAmount: Decimal;
	fanTokenOfferingId: string;
	fanTokenShortCode: string;
	isEnoughBuyMax: boolean;
	maxAmount: Decimal;
	maxReason: string | undefined;
	minAmount: Decimal;
	partnerId: string;
	partnerLogo: string;
	partnerName: string;
	poolWalletAddress?: string;
	processExpiresAt: string;
	processStartedAt: string;
	voteTokenId: string;
};

export type FanTokenOfferingMerkleProofFetchParams = {
	fanTokenOfferingId: string;
};

export type FanTokenOfferingLobbyDataWithPartner = FetchFanTokenOfferingLobbyByVoteTokenOfferingIdData & {
	data: {
		voteToken: {
			partner?: {
				id: string;
				images: [
					{
						imageType: {
							type: string;
						};
						url: string;
					}
				];
				name: string;
			};
		};
	};
};

export enum FanTokenGoatImage {
	GoatAlertImage = 'goatAlertImage',
	GoatAnkwardImage = 'goatAnkwardImage'
}
export enum FanTokenErrorModalButtonBehaviour {
	RedirectToFirstPage = 'redirectToFirstPage',
	RedirectToMainApp = 'redirectToMainApp'
}

export type FTOErrorModalParams =
	| {
			buttonText: string;
			description: string;
			image: Image3DType;
			onMainButtonPress: () => void;
			onMainButtonPressBehaviour?: FanTokenErrorModalButtonBehaviour;
			onSecondaryButtonPress?: () => void;
			onSecondaryButtonPressBehaviour?: FanTokenErrorModalButtonBehaviour;
			secondaryActionButtonText?: string;
			testID: string;
			title: string;
	  }
	| {
			buttonText: string;
			description: string;
			image: Image3DType;
			onMainButtonPress?: never;
			onMainButtonPressBehaviour: FanTokenErrorModalButtonBehaviour;
			onSecondaryButtonPress?: () => void;
			onSecondaryButtonPressBehaviour?: FanTokenErrorModalButtonBehaviour;
			secondaryActionButtonText?: string;
			testID: string;
			title: string;
	  };

export enum FTOErrorCodes {
	AmountNotAvailable = '0094708e-4121-4f41-b90b-ab6daf9b27ce',
	DefaultError = 'default Error',
	NotOpen = '7de91dff-3195-4db9-b357-52315680cbeb',
	SoldOut = '47045476-2083-40db-99c3-6726747b7d09',
	TooManyRequests = '390faefc-8281-4568-9589-7bba246ffc4f'
}

export type OrderReviewData = {
	altCurrency: string;
	defaultCurrency: string;
	defaultCurrencyType?: PaymentCurrencyType;
	defaultMainLedgerAmount: Decimal;
	fanTokenOfferingId?: string | undefined;
	fanTokenShortCode: string;
	mainLedgerIsAvailable?: boolean;
	partnerLogo?: string;
	partnerName: string;
	processExpiresAt: string;
};

export type OrderReviewAmounts = {
	altCurrencyAmount?: number;
	altCurrencyAmountWithFeeLessWalletBalance?: number;
	defaultCurrencyAmount?: Decimal;
	defaultCurrencyAmountWithFeeLessWalletBalance?: Decimal;
	defaultCurrencyAmountWithoutSlippage?: Decimal;
	defaultCurrencySlippageAmount?: number;
	exchangesData?: CostEstimateData['exchangesData'];
	payload?: FanTokenOfferingCostEstimate['payload'];
	purchaseAmount: Decimal;
	serviceFee?: string;
	slippagePercentage?: string;
	totalAmountInAltCurrency?: number;
	totalAmountInDefaultCurrency?: Decimal;
	totalUsedWalletBalance: Decimal;
	totalUsedWalletBalanceInAltCurrency?: number;
	tradingFee?: Decimal;
	tradingFeeInAltCurrency?: number;
	tradingFiatAmount?: number;
	tradingFiatVatAmount?: number;
	tradingVatAmount?: Decimal;
};
